<template>
  <div class="row blog-page">
    <!-- Articles -->
    <div class="col-lg-9 col-md-12">
      <div class="card">
        <div :class="(loading ? 'show-loader' : 'hide-loader')">
          <div class="el-loading-spinner">
            <svg viewBox="25 25 50 50" class="circular">
              <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
            </svg>
          </div>
        </div>
        <div class="card-content">
          <div class="blog-toolbar">
            <h5 class="blog-card-title">{{ $t('blogPage.articles') }}</h5>
            <input type="text" class="form-control blog-search"
                   v-model="filters.search"
                   :placeholder="$t('blogPage.searchByTitle')"
                   @input="onSearchInput" />
            <el-select v-model="filters.category_id" class="blog-filter" @change="reloadArticles">
              <el-option :label="$t('blogPage.allCategories')" value=""></el-option>
              <el-option :label="$t('blogPage.noCategory')" value="none"></el-option>
              <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="String(cat.id)"></el-option>
            </el-select>
            <el-select v-model="filters.status" class="blog-filter" @change="reloadArticles">
              <el-option :label="$t('blogPage.allStatuses')" value=""></el-option>
              <el-option :label="$t('blogPage.published')" value="1"></el-option>
              <el-option :label="$t('blogPage.draft')" value="0"></el-option>
            </el-select>
            <button class="btn add blog-add" @click="openCreate">
              <img src="/static/img/icons/add.png">
              {{ $t('blogPage.addArticle') }}
            </button>
          </div>

          <div class="blog-table-wrap">
            <table class="table width-table blog-table">
              <thead>
                <tr>
                  <th width="9%">{{ $t('blogPage.cover') }}</th>
                  <th width="37%">{{ $t('blogPage.title') }}</th>
                  <th width="14%">{{ $t('blogPage.category') }}</th>
                  <th width="12%">{{ $t('blogPage.author') }}</th>
                  <th width="10%">{{ $t('blogPage.publishedAt') }}</th>
                  <th width="9%">{{ $t('blogPage.status') }}</th>
                  <th width="9%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in articles" :key="item.id" class="blog-row" :title="$t('general.edit')" @click="openEdit(item)">
                  <td>
                    <img v-if="item.image_url" :src="item.image_url" class="blog-thumb" />
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <div class="blog-clamp" :title="item.title">{{ item.title }}</div>
                    <small class="text-muted blog-slug">/{{ item.slug }}</small>
                  </td>
                  <td>{{ item.category ? item.category.name : '—' }}</td>
                  <td>{{ item.author_name || '—' }}</td>
                  <td class="blog-nowrap">
                    <template v-if="item.published_at">
                      {{ formatDay(item.published_at) }}
                      <small class="text-muted blog-time">{{ formatTime(item.published_at) }}</small>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span class="blog-badge" :class="'blog-badge--' + articleStatus(item)">
                      {{ $t('blogPage.' + articleStatus(item)) }}
                    </span>
                  </td>
                  <td>
                    <div class="blog-actions">
                      <a class="btn btn-danger btn-icon delete" @click="deleteArticle(item, $event)" :title="$t('general.delete')">
                        <img src="/static/img/icons/icon-close.png">
                        {{ $t('general.delete') }}
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="!loading && articles.length === 0" class="text-center mar-tp-20">
            {{ $t('blogPage.noArticles') }}
          </p>
          <el-pagination
            v-if="pagination.total > pagination.per_page"
            class="active-pagination blog-pagination"
            :total="pagination.total"
            :page-size="pagination.per_page"
            :current-page="pagination.page"
            layout="prev, pager, next, total"
            @current-change="handlePageChange">
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- Categories (optional) -->
    <div class="col-lg-3 col-md-12">
      <div class="card">
        <div class="card-content">
          <h5 class="blog-card-title">{{ $t('blogPage.categories') }}</h5>
          <p class="text-muted blog-hint">{{ $t('blogPage.categoriesHint') }}</p>
          <div class="blog-cat-add">
            <input type="text" class="form-control"
                   v-model="newCategoryName"
                   :maxlength="CATEGORY_NAME_MAX"
                   :placeholder="$t('blogPage.categoryName')"
                   @keyup.enter="addCategory" />
            <button class="btn btn-success btn-icon" :disabled="savingCategory" @click="addCategory" :title="$t('general.add')">
              <i class="fa fa-plus"></i>
            </button>
          </div>
          <ul class="blog-cat-list">
            <li v-for="cat in categories" :key="cat.id">
              <template v-if="editingCategoryId === cat.id">
                <input type="text" class="form-control"
                       v-model="editingCategoryName"
                       :maxlength="CATEGORY_NAME_MAX"
                       @keyup.enter="saveCategory(cat)"
                       @keyup.esc="cancelEditCategory" />
                <a class="blog-cat-action text-success" @click="saveCategory(cat)" :title="$t('general.save')"><i class="fa fa-check"></i></a>
                <a class="blog-cat-action" @click="cancelEditCategory" :title="$t('general.cancel')"><i class="fa fa-times"></i></a>
              </template>
              <template v-else>
                <span class="blog-cat-name" :title="cat.name">{{ cat.name }}</span>
                <span class="blog-cat-count">{{ cat.articles_count || 0 }}</span>
                <a class="blog-cat-action" @click="startEditCategory(cat)" :title="$t('general.edit')"><i class="fa fa-pencil"></i></a>
                <a class="blog-cat-action text-danger" @click="deleteCategory(cat)" :title="$t('general.delete')"><i class="fa fa-trash"></i></a>
              </template>
            </li>
          </ul>
          <p v-if="!categories.length" class="text-muted text-center">{{ $t('blogPage.noCategories') }}</p>
        </div>
      </div>
    </div>

    <!-- Add / Edit article modal -->
    <vmodal name="blogArticleModal"
            class="blogArticleModal"
            width="95%"
            :maxWidth="1100"
            :minWidth="300"
            height="auto"
            :scrollable="true"
            :adaptive="true"
            :clickToClose="false"
            @opened="onModalOpened">
      <div class="header border-bottom">
        <h4>{{ form.id ? $t('blogPage.editArticle') : $t('blogPage.addArticle') }}</h4>
      </div>
      <div class="content-group">
        <div class="row">
          <!-- Main content -->
          <div class="col-md-8">
            <div class="form-group">
              <label>{{ $t('blogPage.title') }} *</label>
              <input type="text" class="form-control" v-model="form.title" :maxlength="TITLE_MAX" />
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.slug') }}</label>
              <input type="text" class="form-control" v-model="form.slug" :maxlength="SLUG_MAX" :placeholder="slugPreview" />
              <small class="text-muted">{{ $t('blogPage.slugHint') }}</small>
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.shortDescription') }}</label>
              <textarea class="form-control" rows="3" v-model="form.short_description" :maxlength="SHORT_DESCRIPTION_MAX"></textarea>
              <small class="text-muted pull-right">{{ (form.short_description || '').length }} / {{ SHORT_DESCRIPTION_MAX }}</small>
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.content') }}</label>
              <wysiwyg v-if="editorReady" v-model="form.content" :options="editorOptions" class="form-control blog-editor" />
            </div>
          </div>

          <!-- Side: cover, meta, SEO -->
          <div class="col-md-4">
            <div class="form-group">
              <label>{{ $t('blogPage.status') }}</label>
              <div class="blog-status-switch">
                <p-switch v-model="form.is_active" type="success"></p-switch>
                <span>{{ form.is_active ? $t('blogPage.published') : $t('blogPage.draft') }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.publishedAt') }}</label>
              <el-date-picker
                v-model="form.published_at"
                type="datetime"
                format="yyyy-MM-dd HH:mm"
                value-format="yyyy-MM-dd HH:mm:ss"
                class="w-100">
              </el-date-picker>
              <small class="text-muted">{{ $t('blogPage.publishedAtHint') }}</small>
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.category') }}</label>
              <el-select v-model="form.blog_category_id" clearable class="w-100" :placeholder="$t('blogPage.noCategory')">
                <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id"></el-option>
              </el-select>
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.author') }}</label>
              <input type="text" class="form-control" v-model="form.author_name" :maxlength="AUTHOR_MAX" />
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.cover') }} <small class="text-muted">({{ $t('blogPage.coverHint') }})</small></label>
              <el-upload
                action=""
                list-type="picture-card"
                accept=".jpg,.jpeg,.png"
                :file-list="form.imagelist"
                :limit="1"
                :auto-upload="false"
                :on-change="handleImageChange"
                :on-remove="handleImageRemove"
                :on-exceed="handleExceed">
                <img src="/static/img/icons/icon-plus-primary.png">
              </el-upload>
            </div>
            <div class="form-group">
              <label>{{ $t('blogPage.imageAlt') }}</label>
              <input type="text" class="form-control" v-model="form.image_alt" :maxlength="IMAGE_ALT_MAX" />
            </div>

            <div class="blog-seo">
              <a class="blog-seo-toggle" @click="seoOpen = !seoOpen">
                <i class="fa" :class="seoOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                {{ $t('blogPage.seo') }}
              </a>
              <div v-show="seoOpen" class="blog-seo-body">
                <div class="form-group">
                  <label>{{ $t('blogPage.metaTitle') }}</label>
                  <input type="text" class="form-control" v-model="form.meta_title" :maxlength="META_TITLE_MAX" :placeholder="form.title" />
                  <small class="text-muted pull-right">{{ (form.meta_title || '').length }} / 60</small>
                </div>
                <div class="form-group">
                  <label>{{ $t('blogPage.metaDescription') }}</label>
                  <textarea class="form-control" rows="3" v-model="form.meta_description" :maxlength="META_DESCRIPTION_MAX" :placeholder="form.short_description"></textarea>
                  <small class="text-muted pull-right">{{ (form.meta_description || '').length }} / 160</small>
                </div>
                <div class="form-group">
                  <label>{{ $t('blogPage.metaKeywords') }}</label>
                  <input type="text" class="form-control" v-model="form.meta_keywords" :maxlength="META_KEYWORDS_MAX" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="blogArticleModal__footer border-top">
        <button type="button" class="btn btn-default mar-rt-10" @click="closeModal">
          {{ $t('general.cancel') }}
        </button>
        <button type="button" class="btn btn-success btn-icon save" :disabled="saving" @click="saveArticle" :title="$t('general.save')">
          <img src="/static/img/icons/save.png">
          {{ $t('general.save') }}
        </button>
      </div>
    </vmodal>
  </div>
</template>

<script>
  import Vue from 'vue'
  import { Upload, Select, Option, Pagination, DatePicker } from 'element-ui'
  import vmodal from 'vue-js-modal'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import 'vue-wysiwyg/dist/vueWysiwyg.css'

  Vue.use(Upload)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(Pagination)
  Vue.use(DatePicker)
  Vue.use(vmodal, { componentName: 'vmodal' })

  // Field limits, must match the API (BlogArticleController / BlogCategoryController).
  const TITLE_MAX = 255
  const SLUG_MAX = 255
  const SHORT_DESCRIPTION_MAX = 500
  const AUTHOR_MAX = 150
  const IMAGE_ALT_MAX = 255
  const META_TITLE_MAX = 255
  const META_DESCRIPTION_MAX = 300
  const META_KEYWORDS_MAX = 500
  const CATEGORY_NAME_MAX = 100
  const MAX_IMAGE_MB = 5
  const PER_PAGE = 20

  export default {
    components: {
      PSwitch
    },
    data () {
      return {
        TITLE_MAX,
        SLUG_MAX,
        SHORT_DESCRIPTION_MAX,
        AUTHOR_MAX,
        IMAGE_ALT_MAX,
        META_TITLE_MAX,
        META_DESCRIPTION_MAX,
        META_KEYWORDS_MAX,
        CATEGORY_NAME_MAX,
        // Only the cover image is allowed: no inline images (they'd be stored
        // as base64 inside the content). The editor options are merged
        // shallowly, so the global hidden modules are repeated here.
        editorOptions: {
          hideModules: { code: true, table: true, image: true }
        },
        loading: true,
        saving: false,
        savingCategory: false,
        editorReady: false,
        seoOpen: false,
        articles: [],
        categories: [],
        filters: {
          search: '',
          category_id: '',
          status: ''
        },
        pagination: {
          total: 0,
          page: 1,
          per_page: PER_PAGE
        },
        searchTimer: null,
        newCategoryName: '',
        editingCategoryId: null,
        editingCategoryName: '',
        form: this.emptyForm()
      }
    },
    computed: {
      companyID () {
        return this.$session.get('selectedCompany')
      },
      token () {
        return this.$session.get('token')
      },
      slugPreview () {
        return this.slugify(this.form.title)
      }
    },
    created () {
      this.getCategories()
      this.getArticles()
    },
    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },
    methods: {
      emptyForm () {
        return {
          id: null,
          title: '',
          slug: '',
          short_description: '',
          content: '',
          blog_category_id: null,
          author_name: '',
          published_at: null,
          is_active: false,
          image_alt: '',
          meta_title: '',
          meta_description: '',
          meta_keywords: '',
          imagelist: []
        }
      },
      baseData (extra) {
        return Object.assign({
          token: this.token,
          company_id: this.companyID
        }, extra || {})
      },
      firstError (response) {
        if (!response.errors) {
          return this.$t('validations.wentWrong')
        }
        const first = Object.values(response.errors)[0]
        return Array.isArray(first) ? first[0] : first
      },
      /* Same rules as the API str_slug, only used for the placeholder preview. */
      slugify (text) {
        return (text || '')
          .normalize('NFD').replace(/[̀-ͯ]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/[\s-]+/g, '-')
      },
      /* '2026-10-08 14:30:00' -> '08.10.2026' */
      formatDay (value) {
        const [y, m, d] = value.substring(0, 10).split('-')
        return d + '.' + m + '.' + y
      },
      /* '2026-10-08 14:30:00' -> '14:30' */
      formatTime (value) {
        return value.substring(11, 16)
      },
      /* published / scheduled (future date) / draft */
      articleStatus (item) {
        if (!item.is_active) {
          return 'draft'
        }
        if (item.published_at && new Date(item.published_at.replace(' ', 'T')) > new Date()) {
          return 'scheduled'
        }
        return 'published'
      },
      /* ---------------- Categories ---------------- */
      getCategories () {
        this.apiRequest('/blog/categories', 'GET', this.baseData(), (response) => {
          if (response.status === true) {
            this.categories = response.data
          }
        })
      },
      addCategory () {
        const name = (this.newCategoryName || '').trim()
        if (!name || this.savingCategory) {
          return
        }
        this.savingCategory = true
        this.apiRequest('/blog/categories', 'POST', this.baseData({ name }), (response) => {
          this.savingCategory = false
          if (response.status === true) {
            this.newCategoryName = ''
            this.getCategories()
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      startEditCategory (cat) {
        this.editingCategoryId = cat.id
        this.editingCategoryName = cat.name
      },
      cancelEditCategory () {
        this.editingCategoryId = null
        this.editingCategoryName = ''
      },
      saveCategory (cat) {
        const name = (this.editingCategoryName || '').trim()
        if (!name) {
          return
        }
        this.apiRequest('/blog/categories/' + cat.id, 'PUT', this.baseData({ name }), (response) => {
          if (response.status === true) {
            this.cancelEditCategory()
            this.getCategories()
            // Category names are shown in the articles table too.
            this.getArticles()
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      deleteCategory (cat) {
        swal({
          text: this.$t('blogPage.deleteCategoryConfirm', { name: cat.name, count: cat.articles_count || 0 }),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('general.yes')
        }).then((result) => {
          if (result) {
            this.apiRequest('/blog/categories/' + cat.id, 'DELETE', this.baseData(), (response) => {
              if (response.status === true) {
                if (this.filters.category_id === String(cat.id)) {
                  this.filters.category_id = ''
                }
                this.getCategories()
                this.getArticles()
              } else {
                swal('', this.firstError(response), 'error').catch(() => {})
              }
            })
          }
        }).catch(() => {})
      },
      /* ---------------- Articles list ---------------- */
      getArticles () {
        this.loading = true
        const data = this.baseData({
          page: this.pagination.page,
          per_page: this.pagination.per_page,
          search: this.filters.search,
          category_id: this.filters.category_id,
          status: this.filters.status
        })
        this.apiRequest('/blog/articles', 'GET', data, (response) => {
          if (response.status === true && response.data) {
            this.articles = response.data.items || []
            const p = response.data.pagination || {}
            this.pagination.total = p.total || 0
            this.pagination.page = p.page || 1
          }
          this.$nextTick(() => { this.loading = false })
        })
      },
      reloadArticles () {
        this.pagination.page = 1
        this.getArticles()
      },
      onSearchInput () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(this.reloadArticles, 400)
      },
      handlePageChange (page) {
        this.pagination.page = page
        this.getArticles()
      },
      deleteArticle (item, event) {
        if (event) event.stopPropagation()
        swal({
          text: this.$t('blogPage.deleteArticleConfirm', { name: item.title }),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('general.yes')
        }).then((result) => {
          if (result) {
            this.apiRequest('/blog/articles/' + item.id, 'DELETE', this.baseData(), (response) => {
              if (response.status === true) {
                // Step back a page when the last row of a page is deleted.
                if (this.articles.length === 1 && this.pagination.page > 1) {
                  this.pagination.page--
                }
                this.getArticles()
                this.getCategories()
                swal('', this.$t('general.success') + '!', 'success').catch(() => {})
              } else {
                swal('', this.firstError(response), 'error').catch(() => {})
              }
            })
          }
        }).catch(() => {})
      },
      /* ---------------- Modal open / close ---------------- */
      openCreate () {
        this.form = this.emptyForm()
        this.seoOpen = false
        this.showModal()
      },
      openEdit (item) {
        this.apiRequest('/blog/articles/' + item.id, 'GET', this.baseData(), (response) => {
          if (response.status === true) {
            this.form = this.mapToForm(response.data)
            this.seoOpen = !!(this.form.meta_title || this.form.meta_description || this.form.meta_keywords)
            this.showModal()
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      showModal () {
        // Re-mount the editor so it picks up the article's HTML.
        this.editorReady = false
        this.$modal.show('blogArticleModal')
        this.$nextTick(() => { this.editorReady = true })
      },
      closeModal () {
        this.$modal.hide('blogArticleModal')
      },
      onModalOpened () {
        // `.main-panel` has a transform, which makes it the containing block for
        // this modal's `position: fixed` container. Re-parent it to <body> so it
        // stays viewport-fixed instead of sliding under the fixed sidebar.
        const el = document.querySelector('.blogArticleModal.vm--container')
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el)
        }
      },
      mapToForm (data) {
        const form = this.emptyForm()
        form.id = data.id
        form.title = data.title || ''
        form.slug = data.slug || ''
        form.short_description = data.short_description || ''
        form.content = data.content || ''
        form.blog_category_id = data.blog_category_id || null
        form.author_name = data.author_name || ''
        form.published_at = data.published_at || null
        form.is_active = !!data.is_active
        form.image_alt = data.image_alt || ''
        form.meta_title = data.meta_title || ''
        form.meta_description = data.meta_description || ''
        form.meta_keywords = data.meta_keywords || ''
        form.imagelist = data.image_filename
          ? [{ url: data.image_url, name: data.image_filename, filename: data.image_filename }]
          : []
        return form
      },
      /* ---------------- Cover image ---------------- */
      handleImageChange (file, fileList) {
        if (file.size && (file.size / 1024 / 1024) > MAX_IMAGE_MB) {
          swal({
            toast: true,
            type: 'warning',
            text: this.$t('validations.pictureSizeExceed'),
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          }).catch(() => {})
          this.form.imagelist = fileList.filter(f => f.uid !== file.uid).slice(-1)
          return
        }
        this.form.imagelist = fileList.slice(-1)
      },
      handleImageRemove () {
        this.form.imagelist = []
      },
      handleExceed () {
        swal('', this.$t('validations.pictureSizeExceed'), 'warning').catch(() => {})
      },
      async collectImage () {
        const f = this.form.imagelist && this.form.imagelist[0]
        if (!f) {
          return ''
        }
        if (f.raw) {
          return this.getBase64(f.raw)
        }
        return f.filename || ''
      },
      /* ---------------- Save ---------------- */
      async saveArticle () {
        if (!(this.form.title || '').trim()) {
          swal('', this.$t('blogPage.titleRequired'), 'warning').catch(() => {})
          return
        }

        this.saving = true

        let image
        try {
          image = await this.collectImage()
        } catch (e) {
          this.saving = false
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          return
        }

        const data = this.baseData({
          title: this.form.title,
          slug: this.form.slug,
          short_description: this.form.short_description,
          content: this.form.content,
          blog_category_id: this.form.blog_category_id || '',
          author_name: this.form.author_name,
          published_at: this.form.published_at || '',
          is_active: this.form.is_active ? 1 : 0,
          image: image,
          image_alt: this.form.image_alt,
          meta_title: this.form.meta_title,
          meta_description: this.form.meta_description,
          meta_keywords: this.form.meta_keywords
        })

        const isUpdate = !!this.form.id
        const url = isUpdate ? '/blog/articles/' + this.form.id : '/blog/articles'
        const method = isUpdate ? 'PUT' : 'POST'

        this.apiRequest(url, method, data, (response) => {
          this.saving = false
          if (response.status === true) {
            this.closeModal()
            this.getArticles()
            this.getCategories()
            swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      }
    }
  }
</script>

<style scoped>
  .border-bottom {
    border-bottom: 1px solid #ccc;
  }
  .border-top {
    border-top: 1px solid #ccc;
  }
  .header {
    padding: 15px;
  }
  .blogArticleModal >>> .vm--modal {
    top: 0 !important;
    left: 0 !important;
    width: 96% !important;
    max-width: 1100px !important;
    max-height: 90vh !important;
    margin: 20px auto !important;
    display: flex;
    flex-direction: column;
  }
  .content-group {
    margin: 10px 15px;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    /* Right padding keeps the side column off the scrollbar (.row has -15px margins). */
    padding: 0 20px 24px 0;
  }
  .blogArticleModal__footer {
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .w-100 {
    width: 100% !important;
  }
  .mar-tp-20 {
    margin-top: 20px;
  }
  .blog-card-title {
    margin: 0 0 8px 0;
    font-weight: 600;
  }
  .blog-hint {
    font-size: 12px;
  }
  /* Toolbar: title, search, filters and Add button on one wrapping line. */
  .blog-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
  }
  .blog-toolbar .blog-card-title {
    margin: 0 10px 0 0;
  }
  .blog-search {
    flex: 1 1 200px;
    max-width: 320px;
  }
  .blog-filter {
    width: 180px;
  }
  .blog-add {
    margin-left: auto;
    margin-bottom: 0;
  }
  .blog-table-wrap {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .blog-table-wrap > table {
    min-width: 700px;
  }
  .blog-nowrap {
    white-space: nowrap;
  }
  .blog-time {
    display: block;
    font-size: 11px;
  }
  /* The whole row opens the edit modal; only Delete stops the click. */
  .blog-row {
    cursor: pointer;
    transition: background-color .15s;
  }
  .blog-row:hover >>> td {
    background-color: #eef1fb !important;
  }
  .blog-actions {
    display: flex;
    justify-content: flex-end;
  }
  .blog-actions .btn {
    margin: 0 !important;
    white-space: nowrap;
  }
  .blog-thumb {
    width: 64px;
    height: 34px;
    object-fit: cover;
    border-radius: 4px;
  }
  .blog-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }
  .blog-slug {
    display: block;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .blog-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    white-space: nowrap;
  }
  .blog-badge--published {
    background: #e3f6ea;
    color: #2e8b57;
  }
  .blog-badge--scheduled {
    background: #fff4dd;
    color: #b7791f;
  }
  .blog-badge--draft {
    background: #eef1f7;
    color: #5a6a8a;
  }
  .blog-pagination {
    margin-top: 15px;
  }
  /* Categories card */
  .blog-cat-add {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
  .blog-cat-add .btn {
    margin: 0;
    padding: 6px 12px;
  }
  .blog-cat-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .blog-cat-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
  }
  .blog-cat-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .blog-cat-count {
    font-size: 12px;
    color: #7C8DB5;
    background: #eef1f7;
    border-radius: 10px;
    padding: 0 8px;
  }
  .blog-cat-action {
    cursor: pointer;
    color: #7C8DB5;
    padding: 0 2px;
  }
  /* Editor */
  .blog-editor {
    height: auto;
    padding: 0;
  }
  .blog-editor >>> .editr--content {
    min-height: 320px;
  }
  .blog-status-switch {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .blog-seo {
    border-top: 1px solid #eee;
    padding-top: 10px;
  }
  .blog-seo-toggle {
    cursor: pointer;
    font-weight: 600;
    display: inline-block;
    margin-bottom: 10px;
  }
  /* Space after the whole page so the floating "new orders" widget has room. */
  .blog-page {
    padding-bottom: 150px;
  }
  @media (max-width: 600px) {
    .blog-search,
    .blog-filter {
      flex: 1 1 100%;
      max-width: none;
      width: 100%;
    }
    .blog-add {
      margin-left: 0;
      width: 100%;
    }
    .content-group {
      margin: 10px;
    }
    .header,
    .blogArticleModal__footer {
      padding: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Online/Blog.vue